import { Employee } from '../types/Employee/Employee'
import { Activity } from '../types/Reports/ShiftEnd/Activity'
import { ShiftEndWorkItemsSummary } from '../types/Reports/ShiftEnd/ShiftEndWorkItems'
import { BucketContributors, TimeSheet } from '../types/Reports/ShiftEnd/TimeSheet'
import { deleteOne, getAll, getOne, patch, post } from './CommonServices/httpService'
import { ForemanDB } from '../types/IndexedDB/ForemanDB'
import {
	confirmTimesheetFB,
	getDailyActivitiesFB,
	getTimeSheetsForSupervisorFB,
	updateTimeSheetActivitiesFB,
	removeRecordFB,
	updateTimeRecordCommentFB,
} from './CacheFallbackServices/shiftEndReportFallback'
import { mapTimeSheets } from './CacheMapperServices/shiftEndMapper'
import { hasConnection } from './CommonServices/healthGate'
import { showErrorToast } from './CommonServices/alertServices'

const getWorkItemsForSupervisor = async (
	supervisorId: number,
	date: Date,
	db: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			return await getDailyActivitiesFB(db)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get work items from cache' })
			throw error
		}
	}
	const result = await getAll<ShiftEndWorkItemsSummary[]>(
		`/TimeSheets/${supervisorId}/WorkItems?Date=${date.toISOString()}`,
	)
	return result?.data
}
const getTimeSheetsForSupervisor = async (
	supervisorId: number,
	date: Date,
	db: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			return await getTimeSheetsForSupervisorFB(db, date)
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to get timesheets from cache' })
			throw error
		}
	}
	const result = await getAll<TimeSheet[]>(
		`/TimeSheets?supervisorId=${supervisorId}&Date=${date.toISOString()}`,
	)
	if (db) {
		mapTimeSheets(db, result?.data)
	}
	return result?.data
}

const getDirectReports = async (
	id: number,
	department: string,
	date: Date,
): Promise<Employee[]> => {
	const result = await getAll<Employee[]>(
		`/Workers/DirectReports/${id}?dept=${department}&date=${date.toISOString()}`,
		'Failed to get Direct Reports',
	)

	return result?.data ?? []
}

const confirmTimeSheet = async (timeSheetId: number, db: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			await confirmTimesheetFB(db, timeSheetId)
			return { success: true }
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to confirm timesheet in offline mode' })
			throw error
		}
	}
	return await patch(`/TimeSheets/${timeSheetId}/Confirm`, {})
}

const updateTimeSheetActivities = async (
	timesheetId: number,
	activities: Activity[],
	db: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			await updateTimeSheetActivitiesFB(db, timesheetId, activities)
			return
		} catch (error: any) {
			showErrorToast({
				message: error.message || 'Failed to update timesheet activities in offline mode',
			})
			throw error
		}
	}
	return await patch(
		`/TimeSheets/${timesheetId}/TimeRecords`,
		activities.map((activity) => ({
			startTime: activity.startTime,
			endTime: activity.endTime,
			id: activity.id,
		})),
	)
}

const removeRecord = async (timesheetId: number, timeRecordId: number, db: ForemanDB | null) => {
	if (!(await hasConnection()) && db) {
		try {
			await removeRecordFB(db, timesheetId, timeRecordId)
			return
		} catch (error: any) {
			showErrorToast({ message: error.message || 'Failed to remove record in offline mode' })
			throw error
		}
	}
	return await deleteOne(`/TimeSheets/${timesheetId}/TimeRecords/${timeRecordId}`)
}

const updateTimeRecordComment = async (
	timesheetId: number,
	timeRecordId: number,
	comment: string,
	db: ForemanDB | null,
) => {
	if (!(await hasConnection()) && db) {
		try {
			await updateTimeRecordCommentFB(db, timesheetId, timeRecordId, comment)
			return
		} catch (error: any) {
			showErrorToast({
				message: error.message || 'Failed to update comment in offline mode',
			})
			throw error
		}
	}
	return await patch(`/TimeSheets/${timesheetId}/TimeRecords/${timeRecordId}/Comment`, {
		comment,
	})
}

const confirmIndirectTimesheet = async (
	employeeId: number,
	startTime: Date,
	endTime: Date,
	shiftDate: Date,
) => {
	return await post(`/TimeSheets/Indirect/${employeeId}?shiftDate=${shiftDate.toISOString()}`, {
		startTime,
		endTime,
	})
}

const getIndirectTimesheet = async (employeeId: number, shiftDate: Date) => {
	return (
		await getOne<TimeSheet | undefined>(
			`/TimeSheets/Indirect/${employeeId}?shiftDate=${shiftDate.toISOString()}`,
		)
	).data
}

const getIndirectBucketContributors = async (
	employeeId: number,
	shiftDate: Date,
	bucket: {
		workItemTaskId?: number | null
		reworkId?: number | null
		constraintId?: number | null
		nProgressibleTypeId?: number | null
		activity: string
		type: string
	},
) => {
	const params = new URLSearchParams()
	params.set('shiftDate', shiftDate.toISOString())
	if (bucket.workItemTaskId != null) params.set('workItemTaskId', String(bucket.workItemTaskId))
	if (bucket.reworkId != null) params.set('reworkId', String(bucket.reworkId))
	if (bucket.constraintId != null) params.set('constraintId', String(bucket.constraintId))
	if (bucket.nProgressibleTypeId != null)
		params.set('nProgressibleTypeId', String(bucket.nProgressibleTypeId))
	if (bucket.activity) params.set('activity', bucket.activity)
	// Only send activityType for non-entity buckets — enriched WI paths must not be used as keys.
	if (
		bucket.workItemTaskId == null &&
		bucket.reworkId == null &&
		bucket.constraintId == null &&
		bucket.nProgressibleTypeId == null &&
		bucket.type
	) {
		params.set('activityType', bucket.type)
	}

	return (
		await getOne<BucketContributors>(
			`/TimeSheets/Indirect/${employeeId}/Contributors?${params.toString()}`,
		)
	).data
}

const revertTimesheet = async (timesheetId: number) => {
	return await patch(`/TimeSheets/${timesheetId}/Revert`, {})
}

const revertTaskProgress = async (taskId: number, localDate: Date, foremanId: number) => {
	return await deleteOne(
		`/WorkItems/Task/${taskId}/Progress?localDate=${encodeURIComponent(
			localDate.toLocaleString(),
		)}&foremanId=${foremanId}`,
	)
}

const createTimesheet = async (
	workerIds: number[],
	localDate: Date,
	foremanId: number,
	copyFromLaborerId?: number,
) => {
	return await post(
		`/TimeSheets/${foremanId}?localDate=${localDate.toISOString()}${
			copyFromLaborerId ? `&copyFromLaborerId=${copyFromLaborerId}` : ''
		}`,
		workerIds,
	)
}

const removeTimesheet = async (timesheetId: number) => {
	return await deleteOne(`/TimeSheets/${timesheetId}`)
}

const createTimeRecord = async (
	timesheetId: number,
	timeRecord: {
		taskId: number
		activityType: 'reworkTask' | 'workItemTask' | 'nonProgressible' | 'constraint'
		comment?: string
	}[],
	localDate: Date,
	beforeTimeRecordId?: number,
) => {
	return await post(
		`/TimeSheets/${timesheetId}/TimeRecords?localDate=${localDate.toDateString()}${
			beforeTimeRecordId ? `&beforeTimeRecord=${beforeTimeRecordId}` : ''
		}`,
		timeRecord,
	)
}

export {
	getWorkItemsForSupervisor,
	getTimeSheetsForSupervisor,
	confirmTimeSheet,
	updateTimeSheetActivities,
	removeRecord,
	updateTimeRecordComment,
	getDirectReports,
	confirmIndirectTimesheet,
	getIndirectTimesheet,
	getIndirectBucketContributors,
	revertTimesheet,
	revertTaskProgress,
	createTimesheet,
	removeTimesheet,
	createTimeRecord,
}
